// src/hooks/useLocalBills.ts
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { generateBillId } from '../utils/generateId';
import { Bill } from '../types';

const BILLS_STORAGE_KEY = 'bill-tracker-bills';
const PAST_DUE_STORAGE_KEY = 'bill-tracker-past-due';

export interface UseLocalBillsReturn {
	bills: Bill[];
	pastDueBills: Bill[];
	setBills: Dispatch<SetStateAction<Bill[]>>;
	setPastDueBills: Dispatch<SetStateAction<Bill[]>>;
	addBill: (newBill: Bill) => void;
	updateBill: (updatedBill: Bill) => void;
	toggleBillPaid: (billId: string) => void;
	deleteBill: (billId: string) => void;
	markPastDueBillPaid: (billId: string) => void;
	reorderBills: (newOrder: Bill[]) => void;
	getPastDueBillsTotalAmount: () => number;
}

export function useLocalBills(): UseLocalBillsReturn {
	const [bills, setBills] = useState<Bill[]>(() => {
		const savedBills = localStorage.getItem(BILLS_STORAGE_KEY);
		return savedBills ? JSON.parse(savedBills) : [];
	});

	const [pastDueBills, setPastDueBills] = useState<Bill[]>(() => {
		const savedPastDue = localStorage.getItem(PAST_DUE_STORAGE_KEY);
		return savedPastDue ? JSON.parse(savedPastDue) : [];
	});

	// Save bills to localStorage whenever they change
	useEffect(() => {
		localStorage.setItem(BILLS_STORAGE_KEY, JSON.stringify(bills));
	}, [bills]);

	// Save past due bills to localStorage whenever they change
	useEffect(() => {
		localStorage.setItem(
			PAST_DUE_STORAGE_KEY,
			JSON.stringify(pastDueBills)
		);
	}, [pastDueBills]);

	// Check for bills that need to be moved to past due and reset for new month
	useEffect(() => {
		const checkBillsAndCreateRecurring = () => {
			const today = new Date();
			const firstOfMonth = new Date(
				today.getFullYear(),
				today.getMonth(),
				1
			);

			setBills((currentBills) => {
				const newBills: Bill[] = [];
				const billsToPastDue: Bill[] = [];

				currentBills.forEach((bill) => {
					// Bills without due dates are kept as-is
					if (!bill.dueDate) {
						newBills.push(bill);
						return;
					}

					const dueDate = new Date(bill.dueDate);
					const isFromPreviousMonth = dueDate < firstOfMonth;

					if (!isFromPreviousMonth) {
						// Bill is from current month - keep it as-is
						newBills.push(bill);
						return;
					}

					// Bill is from a previous month - handle based on type and paid status
					if (bill.recurring) {
						// RECURRING BILLS:
						// - If unpaid: move to past due
						// - Always: create new bill for current month (reset)
						if (!bill.paid) {
							billsToPastDue.push({
								...bill,
								movedToPastDue: new Date().toISOString(),
							});
						}

						// Calculate the next due date (advance month while keeping same day)
						const nextDueDate = new Date(dueDate);
						nextDueDate.setMonth(nextDueDate.getMonth() + 1);

						// If still in the past, keep advancing until we reach current month
						while (nextDueDate < firstOfMonth) {
							nextDueDate.setMonth(nextDueDate.getMonth() + 1);
						}

						newBills.push({
							...bill,
							id: generateBillId(),
							dueDate: nextDueDate.toISOString(),
							paid: false,
							previousBillId: bill.id,
						});
					} else {
						// NON-RECURRING BILLS:
						// - If unpaid: move to past due
						// - If paid: remove from bills (it's complete)
						if (!bill.paid) {
							billsToPastDue.push({
								...bill,
								movedToPastDue: new Date().toISOString(),
							});
						}
						// Don't add to newBills - bill is removed either way
					}
				});

				// Update past due bills if we found any
				if (billsToPastDue.length > 0) {
					setPastDueBills((current) => [
						...billsToPastDue,
						...current,
					]);
				}

				return newBills;
			});
		};

		// Check on component mount and set up daily check
		checkBillsAndCreateRecurring();

		// Check once per day
		const interval = setInterval(
			checkBillsAndCreateRecurring,
			24 * 60 * 60 * 1000
		);

		return () => clearInterval(interval);
	}, []);

	const addBill = (newBill: Bill): void => {
		setBills((prevBills) => [newBill, ...prevBills]);
	};

	const updateBill = (updatedBill: Bill): void => {
		setBills((prevBills) =>
			prevBills.map((bill) =>
				bill.id === updatedBill.id ? updatedBill : bill
			)
		);
	};

	const toggleBillPaid = (billId: string): void => {
		setBills((prevBills) =>
			prevBills.map((bill) =>
				bill.id === billId ? { ...bill, paid: !bill.paid } : bill
			)
		);
	};

	const deleteBill = (billId: string): void => {
		setBills((prevBills) => prevBills.filter((bill) => bill.id !== billId));
	};

	const markPastDueBillPaid = (billId: string): void => {
		setPastDueBills((prevBills) =>
			prevBills.map((bill) =>
				bill.id === billId
					? {
							...bill,
							paid: true,
							paidDate: new Date().toISOString(),
					  }
					: bill
			)
		);
	};

	const reorderBills = (newOrder: Bill[]): void => {
		setBills(newOrder);
	};

	const getPastDueBillsTotalAmount = (): number => {
		return pastDueBills
			.filter((bill) => !bill.paid)
			.reduce((total, bill) => total + bill.amount, 0);
	};

	return {
		bills,
		pastDueBills,
		setBills,
		setPastDueBills,
		addBill,
		updateBill,
		toggleBillPaid,
		deleteBill,
		markPastDueBillPaid,
		reorderBills,
		getPastDueBillsTotalAmount,
	};
}
